// /admin/subscriptions — operator console: the INHERTA business pulse.
// MRR/ARR, plan distribution, invoices for the period, the dunning worklist
// ("En cobranza" — who is behind, how long, whether their agent already
// stopped), new clients, churn and the next billing run. Every money row
// links to its client; paid invoices can be refunded here (admins only).
// Data via the getSubscriptions() seam → GET /admin/subscriptions.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  getSubscriptions,
  type Invoice,
  type InvoiceStatus,
  type MovementRow,
  type PastDueRow,
  type PlanKey,
  refundInvoice,
  type RevenueKpi,
} from './subscriptions-data.js'
import { useSession } from '../../auth/session.js'
import {
  Banner,
  Button,
  Card,
  Chip,
  DataTable,
  type DataTableColumn,
  FormField,
  Input,
  Modal,
  PageHeader,
  Spinner,
  toast,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'

// Tiers are data-driven (any key from the `tiers` table), so colour is assigned
// by position rather than by a hardcoded name — a new tier still renders.
const PLAN_PALETTE = ['bg-brand-blue', 'bg-ok', 'bg-[#8a93a3]', 'bg-warn', 'bg-sens'] as const
const planBg = (key: PlanKey, index: number): string =>
  PLAN_PALETTE[index % PLAN_PALETTE.length] ?? 'bg-brand-blue'

const STATUS_TONE: Record<InvoiceStatus, 'ok' | 'warn' | 'sens' | 'draft'> = {
  paid: 'ok',
  due: 'warn',
  overdue: 'sens',
  draft: 'draft',
}

function UpArrow(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-3"
    >
      <path d="m6 15 6-6 6 6" />
    </svg>
  )
}

function ClockIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-3"
    >
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 7v5l3 2" />
    </svg>
  )
}

function MetricCard({ kpi }: { kpi: RevenueKpi }): JSX.Element {
  const toneClass =
    kpi.caption?.tone === 'up'
      ? 'text-ok'
      : kpi.caption?.tone === 'warn'
        ? 'text-warn'
        : 'text-text-2'
  return (
    <div className="rounded-lg border border-line bg-surface px-[18px] py-4">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.05em] text-text-3">
        {kpi.label}
      </div>
      <div className="mt-2 text-[23px] font-extrabold leading-[1.1] tracking-[-0.025em] tabular-nums text-text">
        {kpi.value}
        {kpi.unit && <span className="ml-1 text-[13px] font-semibold text-text-2">{kpi.unit}</span>}
      </div>
      {kpi.caption && (
        <div
          className={`mt-[7px] flex items-center gap-1.5 text-[11.5px] font-medium ${toneClass}`}
        >
          {kpi.caption.tone === 'up' && <UpArrow />}
          {kpi.caption.tone === 'warn' && <ClockIcon />}
          {kpi.caption.text}
        </div>
      )}
    </div>
  )
}

function PanelList({ children }: { children: ReactNode }): JSX.Element {
  return (
    <ul className="[&>li]:border-b [&>li]:border-line [&>li:last-child]:border-b-0">{children}</ul>
  )
}

function MovementItem({ row, sign }: { row: MovementRow; sign: 'up' | 'down' }): JSX.Element {
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <span
        aria-hidden
        className={`grid size-[30px] flex-none place-items-center rounded-lg text-[10.5px] font-bold ${
          row.muted ? 'bg-field text-text-2' : 'bg-[image:var(--brand-gradient)] text-white'
        }`}
      >
        {row.initials}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-semibold text-text">{row.name}</span>
        <span className="mt-px block text-[11px] text-text-3">{row.meta}</span>
      </span>
      <span
        className={`flex-none text-[12px] font-bold tabular-nums ${sign === 'up' ? 'text-ok' : 'text-sens'}`}
      >
        {row.amount}
      </span>
    </li>
  )
}

export function SubscriptionsPage(): JSX.Element {
  const qc = useQueryClient()
  const session = useSession()
  // The refund endpoint needs subscription.approve (admins). Showing the
  // button to an account manager would be an action that always 403s — a
  // painted door — so it renders only for roles the server will accept.
  const canRefund =
    session?.platformRole === 'platform_admin' || session?.platformRole === 'platform_owner'
  const [refundFor, setRefundFor] = useState<Invoice | null>(null)
  const [refundReason, setRefundReason] = useState('')
  const [refundAmount, setRefundAmount] = useState('')
  const [refundBusy, setRefundBusy] = useState(false)
  const [refundError, setRefundError] = useState<string | null>(null)

  async function runRefund(): Promise<void> {
    if (!refundFor) return
    setRefundBusy(true)
    setRefundError(null)
    try {
      await refundInvoice(refundFor.tenantId, refundFor.id, {
        ...(refundReason.trim() ? { reason: refundReason.trim() } : {}),
        ...(refundAmount ? { amountMxn: Math.round(Number(refundAmount) * 100) } : {}),
      })
      await qc.invalidateQueries({ queryKey: ['admin', 'subscriptions'] })
      setRefundFor(null)
      setRefundReason('')
      setRefundAmount('')
      toast.ok('Reembolso enviado', {
        description: 'Stripe lo procesa; el cliente lo ve en su estado de cuenta.',
      })
    } catch (err) {
      setRefundError(apiErrorMessage(err, 'No se pudo hacer el reembolso.'))
    } finally {
      setRefundBusy(false)
    }
  }
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'subscriptions'],
    queryFn: getSubscriptions,
  })

  if (isLoading || !data) {
    return (
      <div data-testid="admin-subscriptions" className="mx-auto w-full max-w-6xl py-10">
        <Spinner label="Cargando suscripciones…" />
      </div>
    )
  }

  const invoiceColumns: DataTableColumn<Invoice>[] = [
    {
      key: 'name',
      label: 'Cliente',
      render: (inv) => (
        <span className="flex items-center gap-2.5">
          <span
            aria-hidden
            className="grid size-7 flex-none place-items-center rounded-md bg-[image:var(--brand-gradient)] text-[10px] font-bold text-white"
          >
            {inv.initials}
          </span>
          <Link
            to={`/admin/tenants/${inv.tenantId}?tab=billing`}
            className="font-semibold text-text underline-offset-2 hover:underline"
          >
            {inv.name}
          </Link>
        </span>
      ),
    },
    {
      key: 'plan',
      label: 'Plan',
      render: (inv) => <span className="text-text-2">{inv.plan}</span>,
    },
    {
      key: 'folio',
      label: 'Folio',
      render: (inv) => <span className="font-mono text-[11px] text-text-3">{inv.folio}</span>,
    },
    {
      key: 'amount',
      label: 'Monto',
      align: 'right',
      render: (inv) => (
        <span
          className={`font-mono font-bold tabular-nums ${inv.overdue ? 'text-sens' : 'text-text'}`}
        >
          {inv.amount}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (inv) => (
        <Chip tone={STATUS_TONE[inv.status]} dot>
          {inv.statusLabel}
        </Chip>
      ),
    },
    // Admins also see the money-back lever, only on money actually taken.
    ...(canRefund
      ? [
          {
            key: 'actions',
            label: '',
            align: 'right',
            render: (inv) =>
              inv.paid ? (
                <Button size="sm" variant="ghost" onClick={() => setRefundFor(inv)}>
                  Reembolsar
                </Button>
              ) : null,
          } satisfies DataTableColumn<Invoice>,
        ]
      : []),
  ]

  return (
    <div data-testid="admin-subscriptions" className="mx-auto w-full max-w-6xl">
      <PageHeader
        title="Suscripciones"
        subtitle={`Ingresos recurrentes, planes y facturas · ${data.period}`}
        actions={
          // CFDI export arrives with Facturama (3b); a button that only toasts
          // "coming soon" is a door painted on a wall, so it isn't here yet.
          // The period is a label, not a control — there is nothing to switch to
          // until we keep more than the current month.
          <span className="text-[12px] font-semibold text-text-2">
            {data.period.charAt(0).toUpperCase() + data.period.slice(1)}
          </span>
        }
      />

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {data.kpis.map((k) => (
          <MetricCard key={k.id} kpi={k} />
        ))}
      </div>

      <div className="mt-4 grid grid-cols-1 items-start gap-4 lg:grid-cols-[1.5fr,1fr]">
        {/* left column */}
        <div className="flex flex-col gap-4">
          <Card>
            <Card.Head title="Distribución por plan" sub={data.plansCaption} />
            {data.plans.length === 0 && (
              <p className="px-[18px] py-6 text-center text-[12.5px] text-text-2">
                Ningún plan tiene suscripciones activas todavía.
              </p>
            )}
            <PanelList>
              {data.plans.map((p, i) => (
                <li key={p.key} className="flex items-center gap-3 px-[18px] py-3">
                  <span
                    aria-hidden
                    className={`grid size-10 flex-none place-items-center rounded-[10px] text-[13px] font-bold text-white ${planBg(p.key, i)}`}
                  >
                    {p.badge}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-bold text-text">{p.name}</span>
                    <span className="mt-px block text-[11px] text-text-3">{p.meta}</span>
                  </span>
                  <span className="flex-none text-right">
                    <span className="block text-[17px] font-extrabold tabular-nums leading-none text-text">
                      {p.clients}
                    </span>
                    <span className="mt-0.5 block text-[10.5px] text-text-3">clientes</span>
                  </span>
                  <span className="hidden flex-none text-right sm:block">
                    <span className="block font-mono text-[13px] font-bold tabular-nums text-text">
                      {p.mrr}
                    </span>
                    <span className="mt-0.5 block text-[10.5px] text-text-3">{p.perUnit}</span>
                  </span>
                </li>
              ))}
            </PanelList>
            <div className="border-t border-line px-[18px] py-4">
              <div className="flex h-[9px] gap-0.5 overflow-hidden rounded-[5px]">
                {data.plans.map((p, i) => (
                  <span
                    key={p.key}
                    className={planBg(p.key, i)}
                    style={{ width: `${p.mrrPct}%` }}
                    aria-hidden
                  />
                ))}
              </div>
              <ul className="mt-2.5 flex flex-col gap-1.5">
                {data.plans.map((p, i) => (
                  <li key={p.key} className="flex items-center gap-2 text-[11.5px]">
                    <span
                      aria-hidden
                      className={`size-[9px] flex-none rounded-[3px] ${planBg(p.key, i)}`}
                    />
                    <span className="flex-1 text-text">{p.name}</span>
                    <span className="font-mono text-[11px] font-semibold tabular-nums text-text-2">
                      {p.mrrPct}% del MRR
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Card>

          <section aria-labelledby="invoices-title">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 id="invoices-title" className="text-[13.5px] font-bold text-text">
                Facturas del periodo
              </h2>
            </div>
            <DataTable
              caption="Facturas del periodo"
              columns={invoiceColumns}
              rows={data.invoices}
            />
          </section>
        </div>

        {/* right column */}
        <div className="flex flex-col gap-4">
          <Card>
            <Card.Head
              title="En cobranza"
              action={
                data.pastDue.length > 0 ? (
                  <span className="text-[11.5px] font-bold text-sens">{data.pastDue.length}</span>
                ) : undefined
              }
            />
            {data.pastDue.length === 0 ? (
              <p className="px-4 py-3 text-[12.5px] text-text-2">
                Nadie en cobranza. Cuando una factura venza, el cliente aparece aquí.
              </p>
            ) : (
              <PanelList>
                {data.pastDue.map((row) => (
                  <PastDueItem key={row.tenantId} row={row} />
                ))}
              </PanelList>
            )}
          </Card>

          <Card>
            <Card.Head
              title="Altas este mes"
              action={
                <span className="text-[11.5px] font-bold text-ok">+{data.newClients.length}</span>
              }
            />
            <PanelList>
              {data.newClients.map((row) => (
                <MovementItem key={row.id} row={row} sign="up" />
              ))}
            </PanelList>
          </Card>

          <Card>
            <Card.Head
              title="Bajas este mes"
              action={
                <span className="text-[11.5px] font-bold text-sens">{data.churn.length}</span>
              }
            />
            <PanelList>
              {data.churn.map((row) => (
                <MovementItem key={row.id} row={row} sign="down" />
              ))}
            </PanelList>
          </Card>

          <Card>
            <Card.Head title="Próxima corrida" />
            <div className="flex items-center gap-3 px-4 py-3">
              <span
                aria-hidden
                className="grid size-[30px] flex-none place-items-center rounded-lg bg-field text-text-2 [&_svg]:size-[15px]"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4.5" width="18" height="17" rx="2.4" />
                  <path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
                </svg>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[12.5px] font-semibold text-text">
                  {data.nextRun ? data.nextRun.date : 'Sin cobros programados'}
                </span>
                <span className="mt-px block text-[11px] text-text-3">
                  {data.nextRun
                    ? data.nextRun.meta
                    : 'Ninguna suscripción activa tiene fecha de renovación.'}
                </span>
              </span>
              {data.nextRun && (
                <span className="flex-none font-mono text-[11.5px] font-semibold tabular-nums text-text-2">
                  {data.nextRun.amount}
                </span>
              )}
            </div>
          </Card>
        </div>
      </div>

      <Modal
        open={refundFor !== null}
        onClose={() => {
          if (!refundBusy) setRefundFor(null)
        }}
        title="Reembolsar factura"
        subtitle={`${refundFor?.folio ?? ''} · ${refundFor?.name ?? ''} · ${refundFor?.amount ?? ''}. Mueve dinero real de regreso; no se puede deshacer.`}
        busy={refundBusy}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRefundFor(null)} disabled={refundBusy}>
              Cancelar
            </Button>
            <Button loading={refundBusy} onClick={() => void runRefund()}>
              Reembolsar
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField
            label="Monto (MXN)"
            htmlFor="refund-amount"
            helper="Déjalo vacío para reembolsar la factura completa."
          >
            <Input
              id="refund-amount"
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value.replace(/[^0-9]/g, ''))}
              inputMode="numeric"
              placeholder="Total"
            />
          </FormField>
          <FormField label="Motivo" htmlFor="refund-reason" helper="Queda en la bitácora.">
            <Input
              id="refund-reason"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder="Doble cobro"
            />
          </FormField>
          {refundError && <Banner tone="warn">{refundError}</Banner>}
        </div>
      </Modal>
    </div>
  )
}

/**
 * One client behind on payment: who, how much, how long — and whether
 * their agent is already stopped, which is the difference between a reminder
 * and a phone call. The whole row lands on the client's Facturación.
 */
function PastDueItem({ row }: { row: PastDueRow }): JSX.Element {
  return (
    <li>
      <Link
        to={`/admin/tenants/${row.tenantId}?tab=billing`}
        className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-hover"
      >
        <span
          aria-hidden
          className="grid size-7 flex-none place-items-center rounded-md bg-[image:var(--brand-gradient)] text-[10px] font-bold text-white"
        >
          {row.initials}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[12.5px] font-semibold text-text">{row.name}</span>
          <span className="block text-[11.5px] text-text-3">
            {row.amount}
            {row.folio !== null && ` · ${row.folio}`}
          </span>
        </span>
        {row.botStopped && (
          <Chip tone="sens" dot>
            Agente detenido
          </Chip>
        )}
        <Chip tone={row.botStopped ? 'sens' : 'warn'} dot>
          {row.daysPastDue === 1 ? '1 día' : `${row.daysPastDue} días`}
        </Chip>
      </Link>
    </li>
  )
}
